import { useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import type { PublicScheduleDto, PublicSchoolDto } from '@motoschool/shared';
import { DEFAULT_LESSON_MINUTES, formatMoney } from '@motoschool/shared';
import { api } from '../lib/api';
import { Avatar, Card, EmptyState, PageLoader } from '../components/ui';
import { PublicTimePicker, type PickedLesson } from '../components/PublicTimePicker';
import { SchoolName } from '../components/SchoolName';
import { GuestBookingSheet } from '../components/GuestBookingSheet';

/**
 * Расписание конкретного инструктора по присланной ссылке.
 * Отличается от общей страницы только тем, что показывает одного инструктора
 * и считает переходы по ссылке — заявка так же не требует регистрации.
 */
export function SharedSchedulePage(): React.JSX.Element {
  const { token = '' } = useParams();
  const [picked, setPicked] = useState<PickedLesson | null>(null);
  const [isSent, setIsSent] = useState(false);

  const { data, isLoading, error } = useQuery({
    queryKey: ['public-schedule', token],
    queryFn: () =>
      api.get<PublicScheduleDto>(`/public/schedule/${token}`, undefined, { anonymous: true }),
    enabled: Boolean(token),
    retry: false,
  });

  const { data: school } = useQuery({
    queryKey: ['public', 'school'],
    queryFn: () => api.get<PublicSchoolDto>('/public/school', undefined, { anonymous: true }),
  });

  if (isLoading) return <PageLoader />;

  if (error || !data) {
    return (
      <div className="mx-auto max-w-md px-4 py-16">
        <EmptyState
          title="Ссылка недействительна"
          description="Возможно, срок её действия истёк. Попросите инструктора прислать новую."
          action={
            <Link to="/schedule" className="font-medium text-brand-600">
              Открыть общее расписание
            </Link>
          }
        />
      </div>
    );
  }

  return (
    <div className="min-h-dvh bg-canvas">
      <header className="safe-top border-b border-line bg-surface px-4 pb-4">
        <div className="mx-auto flex max-w-2xl items-center gap-3">
          <Avatar name={data.teacher.name} />
          <div className="min-w-0">
            <p className="truncate text-lg font-semibold">{data.teacher.name}</p>
            <SchoolName name={data.schoolName} className="block truncate text-xs text-muted" />
          </div>
        </div>
      </header>

      <main className="mx-auto max-w-2xl space-y-5 px-4 py-5">
        {isSent && (
          <div className="rounded-card border border-emerald-200 bg-emerald-50 p-4">
            <p className="font-medium text-emerald-800">Заявка отправлена</p>
            <p className="mt-1 text-sm text-emerald-700">
              Инструктор подтвердит запись и свяжется с вами. Вместе с подтверждением он
              заведёт вам аккаунт и передаст пароль — в личном кабинете будут видны занятия,
              баланс часов и оплаты.
            </p>
          </div>
        )}

        <div>
          <h1 className="text-xl font-semibold">Свободное время</h1>
          <p className="text-sm text-muted">
            Выберите день, начало и сколько часов ездить — регистрация не нужна
          </p>
        </div>

        {data.hourlyRateMinor !== null && (
          <Card className="p-3">
            <p className="text-sm">
              <b>{formatMoney(data.hourlyRateMinor, data.currency)}</b> за час занятий
            </p>
          </Card>
        )}

        <PublicTimePicker
          teachers={[
            {
              teacherId: data.teacher.id,
              hourlyRateMinor: data.hourlyRateMinor,
              free: data.free,
              busy: data.busy,
            },
          ]}
          timeZone={data.timeZone}
          currency={data.currency}
          defaultMinutes={school?.defaultLessonMinutes ?? DEFAULT_LESSON_MINUTES}
          onPick={(lesson) => {
            setIsSent(false);
            setPicked(lesson);
          }}
        />

        <Card className="bg-brand-50/60">
          <p className="text-sm">
            Уже занимаетесь здесь?{' '}
            <Link to="/login" className="font-medium text-brand-600">
              Войдите
            </Link>{' '}
            — увидите свои занятия, баланс часов и историю оплат.
          </p>
        </Card>
      </main>

      <GuestBookingSheet
        lesson={picked === null ? null : { ...picked, teacherName: data.teacher.name }}
        endpoint={`/public/schedule/${token}/requests`}
        timeZone={data.timeZone}
        currency={data.currency}
        onClose={() => setPicked(null)}
        onSuccess={() => {
          setPicked(null);
          setIsSent(true);
        }}
      />
    </div>
  );
}
